import TemplateSurveyState from 'containers/SurveyPage/TemplateSurveyState';
import { transformPropsDeep, escapeHTML } from 'containers/App/utils';
import {
  getEmptyAnswer,
  getEmptyNoneOfTheAboveAnswer,
} from 'components/ScreeningQuestion/utils';
import { FAILURE, REASON_SCREENOUT } from 'components/LogicOutcome/constants';
import { SCREENING_ALLOWED_VALUES } from 'components/ScreeningQuestion/constants';
import { ANSWER_NONE_OF_THE_ABOVE } from 'containers/QuestionnairePage/constants';
import { cloneDeep, find, get } from 'lodash';
import {
  getConceptIds,
  getAttributeIds,
} from 'containers/QuestionnairePage/QuestionGroup/utils';
import { mapQuestionsAndGroupsAsNodes } from 'containers/QuestionnairePage/utils';
import {
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
} from 'containers/Survey/constants';

import { END_OF_SURVEY } from 'components/LogicPathPreview/constants';

import {
  screeningQuestionToNormal,
  addScreeningQuestionWithScreeningQL,
  repointCarryForwardFromScreening,
  shouldUseScreeningQl,
  shouldConvertScreeningToQlQuestions,
  applyScreeningQuestionContentUpdate,
  finalizeConvertedScreeningPlacement,
} from '../utils';
import { AB_TEST_TYPES } from '../constants';

const advertisingTestingGenerator = ({
  template,
  variables,
  surveyType,
  peeqCategoryType,
}) => {
  // extract and prepare user variables
  const abTestType = get(
    find(variables, ['name', 'ab_test_type']),
    'value',
    AB_TEST_TYPES.MONADIC,
  );

  const concepts = get(find(variables, ['name', 'concepts']), 'value', []).map(
    (concept) => ({ ...concept, text: `<div>${concept.text}</div>` }),
  );
  const repetitions =
    abTestType === AB_TEST_TYPES.MONADIC ? 1 : concepts.length;
  const productCategory = get(
    find(variables, ['name', 'product_category']),
    'value',
    {},
  );
  const yourBrand = get(find(variables, ['name', 'your_brand']), 'value', {});
  const competitors = get(
    find(variables, ['name', 'competitors']),
    'value',
    [],
  );

  const replaceCategoryText = (value) =>
    value.replace(
      /\{\{product_category\}\}/gi,
      escapeHTML(productCategory.text),
    );

  const replaceBrandText = (value) =>
    value.replace(/\{\{your_brand\}\}/gi, escapeHTML(yourBrand.text));

  const replaceBrandAndCategory = (value) =>
    replaceCategoryText(replaceBrandText(value));

  const questionnaireTemplate = transformPropsDeep(
    template.questionnaireTemplate,
    { prompt: replaceBrandAndCategory, htmlPrompt: replaceBrandAndCategory },
  );

  const { nodes: advertisingTestingTemplateQuestions, screeningQuestions } =
    mapQuestionsAndGroupsAsNodes(questionnaireTemplate);

  const ABTestGroupQuestion = cloneDeep(advertisingTestingTemplateQuestions[4]);

  // screening answers
  const screeningQuestionClone = cloneDeep(screeningQuestions[0]);
  Object.assign(screeningQuestionClone, {
    answers: [
      { ...getEmptyAnswer(), body: yourBrand.text, media: yourBrand.media },
      ...competitors.map((competitor) => ({
        ...getEmptyAnswer(),
        body: competitor.text,
        media: competitor.media,
      })),
      {
        ...getEmptyNoneOfTheAboveAnswer(),
        screenerType: SCREENING_ALLOWED_VALUES.STRICT_NOT_ALLOWED,
      },
    ],
    media: productCategory.media,
  });

  const Survey = new TemplateSurveyState({
    fetchedQuestionnaire: {
      ...questionnaireTemplate,
      isPeeq: surveyType === SURVEY_TYPE_PEEQ,
      isDistributionLink: surveyType === SURVEY_TYPE_DISTRIBUTION_LINK,
    },
    fetchedTargeting:
      surveyType === SURVEY_TYPE_PEEQ
        ? template.peeqTargetingTemplate
        : template.regularTargetingTemplate,
  });

  // CLEAN UP

  // Remove Concepts and Attributes
  const abTestConceptIds = getConceptIds(ABTestGroupQuestion);
  const attributesIds = getAttributeIds(ABTestGroupQuestion);

  abTestConceptIds.forEach((conceptId) => {
    Survey.questionnaire.removeAbConcept({
      refId: ABTestGroupQuestion.refId,
      conceptRefId: conceptId,
    });
  });
  attributesIds.forEach((attributeId) => {
    Survey.questionnaire.removeGroupAttribute({
      refId: ABTestGroupQuestion.refId,
      attributeRefId: attributeId,
      groupType: ABTestGroupQuestion.type,
    });
  });

  // SET UP
  // add user concepts
  concepts.forEach((concept) => {
    const conceptRefId = Survey.questionnaire.addAbConcept(
      ABTestGroupQuestion.refId,
    );
    if (conceptRefId) {
      Survey.questionnaire.updateAbConcept({
        refId: ABTestGroupQuestion.refId,
        conceptRefId,
        name: concept.name,
      });
    }
  });

  // add attribute and attribute values
  const abAttribute = Survey.questionnaire.addGroupAttribute({
    refId: ABTestGroupQuestion.refId,
    name: '',
    type: '',
    groupType: ABTestGroupQuestion.type,
  });

  concepts.forEach((concept, conceptIndex) => {
    const attributeValue = abAttribute.values[conceptIndex];

    Survey.questionnaire.updateGroupAttributeValue({
      refId: ABTestGroupQuestion.refId,
      attributeRefId: abAttribute.refId,
      attributeValueRefId: attributeValue.refId,
      text: concept.text,
      media: concept.media,
    });
  });

  // set AB type (monadic/sequential)
  Survey.questionnaire.updateAbProperties({
    refId: ABTestGroupQuestion.refId,
    properties: { repetitions },
  });

  // second pass for CF
  Survey.targeting.updateScreeningQuestion(screeningQuestionClone);
  applyScreeningQuestionContentUpdate(Survey, screeningQuestionClone, {
    surveyType,
    peeqCategoryType,
  });
  // if creating a Peeq survey
  // we need to convert the OLD screening questions
  // to the new screening questions with screeningQL

  if (shouldConvertScreeningToQlQuestions({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const dlinkScreener = screeningQuestionToNormal(screeningQuestionClone);
    Survey.questionnaire.addQuestion(dlinkScreener);

    repointCarryForwardFromScreening(
      Survey,
      dlinkScreener.refId,
      screeningQuestionClone.refId,
    );

    Survey.questionnaire.updateQuestion(dlinkScreener);

    // set ADL to screenout
    const firstStatementId = Survey.questionnaire.linkAnswerToQuestion({
      question: dlinkScreener,
      answerRefId: find(dlinkScreener.answers, [
        'type',
        ANSWER_NONE_OF_THE_ABOVE,
      ]).refId,
      targetQuestion: { refId: END_OF_SURVEY },
      context: { questionId: dlinkScreener.refId },
    });
    Survey.questionnaire.updateStatementOutcome({
      nodeRefId: dlinkScreener.refId,
      statementId: firstStatementId,
      outcome: {
        params: { id: END_OF_SURVEY },
        type: FAILURE,
        reason: REASON_SCREENOUT,
      },
    });

    finalizeConvertedScreeningPlacement(Survey, [
      {
        originalRefId: screeningQuestionClone.refId,
        convertedQuestion: dlinkScreener,
      },
    ]);
  } else if (shouldUseScreeningQl({ surveyType, peeqCategoryType })) {
    // add questions
    // remove from question:isScreening
    // and from answers: screenerType, screening,
    const newQLScreener = addScreeningQuestionWithScreeningQL(
      Survey,
      screeningQuestionClone,
    );
    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });

    repointCarryForwardFromScreening(
      Survey,
      newQLScreener.refId,
      screeningQuestionClone.refId,
    );

    applyScreeningQuestionContentUpdate(Survey, newQLScreener, {
      surveyType,
      peeqCategoryType,
    });
    Survey.questionnaire.removeQuestion(screeningQuestionClone);
    Survey.targeting.removeAllScreeningQuestion();
  }

  return { state: Survey.state };
};

export default advertisingTestingGenerator;
